/**
 * Forgetting Curve sparkline — Wave 1.
 * Renders R(t) as an SVG path for the next N days. Pure inline SVG, no D3
 * dependency. Used in TroubleCard drill-down dialog + card detail surfaces.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function ForgettingCurveChart({ questionId, height = 80, horizonDays = 90 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!questionId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/student/forgetting-curve/${questionId}?horizon_days=${horizonDays}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [questionId, horizonDays]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-4" data-testid="forgetting-curve-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Computing your forgetting curve…
      </div>
    );
  }
  if (!data || !data.series || data.series.length === 0) {
    return (
      <div className="text-slate-500 text-xs py-3" data-testid="forgetting-curve-empty">
        Not enough review history yet — answer this card a few more times to see the curve.
      </div>
    );
  }

  const W = 320;
  const H = height;
  const pad = 8;
  const xs = data.series.map((p) => p.day_offset);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const span = Math.max(1, maxX - minX);
  const xScale = (v) => pad + ((v - minX) / span) * (W - 2 * pad);
  const yScale = (r) => pad + (1 - r) * (H - 2 * pad);

  const pathD = data.series
    .map((p, i) => `${i === 0 ? "M" : "L"}${xScale(p.day_offset).toFixed(1)},${yScale(p.retrievability).toFixed(1)}`)
    .join(" ");

  const areaD = `${pathD} L${xScale(maxX).toFixed(1)},${yScale(0).toFixed(1)} L${xScale(minX).toFixed(1)},${yScale(0).toFixed(1)} Z`;

  // "Now" marker at day_offset = 0 (or the nearest point we have)
  const nowR = data.retrievability_now ?? 0;
  const nowX = xScale(0);
  const nowY = yScale(nowR);

  return (
    <div data-testid="forgetting-curve-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none" role="img" aria-label="Forgetting curve">
        {/* Sweet-spot band (0.7-0.9) */}
        <rect x={pad} y={yScale(0.9)} width={W - 2 * pad} height={yScale(0.7) - yScale(0.9)} fill="#fef3c7" opacity="0.5" />
        {/* Area */}
        <path d={areaD} fill="url(#fcGrad)" opacity="0.45" />
        {/* Curve */}
        <path d={pathD} fill="none" stroke="#1E3A8A" strokeWidth="2" />
        {/* Now marker */}
        <line x1={nowX} y1={pad} x2={nowX} y2={H - pad} stroke="#dc2626" strokeWidth="1" strokeDasharray="3 2" />
        <circle cx={nowX} cy={nowY} r="3.5" fill="#dc2626" stroke="white" strokeWidth="1.5" />
        <defs>
          <linearGradient id="fcGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E3A8A" />
            <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 px-1">
        <span>now</span>
        <span>Predicted recall over {horizonDays} days</span>
        <span>+{horizonDays}d</span>
      </div>
      <div className="flex items-center gap-3 text-[11px] mt-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rose-600" /> Today: <strong>{Math.round((data.retrievability_now || 0) * 100)}%</strong>
        </span>
        {data.stability != null && (
          <span className="text-slate-500">
            Stability: <strong className="text-slate-700">{Math.round(data.stability)}d</strong>
          </span>
        )}
        {data.lapses_count > 0 && (
          <span className="text-amber-700">🪤 {data.lapses_count} lapses</span>
        )}
      </div>
    </div>
  );
}
